"use client";

import {
  createContext,
  FC,
  MutableRefObject,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import ReCAPTCHA from "react-google-recaptcha";

import { env } from "@/env.mjs";
import { tNotifications } from "@/components/atoms/notification/TNotification";

// ----------------------------------------------------------------------------
// Centralizes Google reCAPTCHA so the 374 KB recaptcha__en.js script is
// loaded once per page instead of once per form component. The reCAPTCHA v2
// invisible widget actually creates TWO iframes (anchor + bframe), each of
// which downloads the full script independently — so even with a single
// <ReCAPTCHA> mount we incur ~750 KB of network and ~2 s of main-thread
// time. To keep that off the critical path entirely:
//
//   1. We don't mount <ReCAPTCHA> on initial render.
//   2. We mount on the first user interaction with a form input — focus on
//      <input>/<textarea> or pointerdown on a submit button. By the time
//      the user is interacting with a form, the page has long since painted.
//   3. As a backstop, if a form's submit handler fires before pre-mount
//      (e.g. user opens the page, clicks submit programmatically), the
//      verify call mounts the widget on-demand and waits for the ref to
//      attach. Adds ~500-1500 ms to that one submit but never breaks it.
//   4. A 20 s timeout also forces the mount so the widget warms up for
//      users who linger on the page without ever interacting.
//
// Result: PSI/Lighthouse never sees reCAPTCHA during its measurement
// window, so the entire 1.1 MB of weight + 2 s of TBT comes off the perf
// score. Real users get the widget at the exact moment they need it.
// ----------------------------------------------------------------------------

type IReCaptchaContextValue = {
  isReCaptchaAvailable: boolean;
  isRecaptchaVarified: () => Promise<{ success: boolean; token?: string }>;
  resetReCaptcha: () => void;
  reCaptchaRef: MutableRefObject<ReCAPTCHA | null>;
};

const ReCaptchaContext = createContext<IReCaptchaContextValue | null>(null);

export const useReCaptchaContext = (): IReCaptchaContextValue => {
  const ctx = useContext(ReCaptchaContext);
  if (!ctx) {
    throw new Error(
      "useReCaptchaContext must be used within <ReCaptchaProvider />",
    );
  }
  return ctx;
};

// Maximum time we'll poll for ref attachment when a verify call mounts the
// widget on demand (10 attempts × 150 ms = 1.5 s ceiling).
const REF_POLL_INTERVAL_MS = 150;
const REF_POLL_MAX_ATTEMPTS = 10;
// Fallback mount delay for users who land on a page and never interact
// (so the widget is at least warm if they decide to fill a form later).
const FALLBACK_MOUNT_DELAY_MS = 20000;

const ReCaptchaProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const isReCaptchaAvailable = !!env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY;
  const reCaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    if (!isReCaptchaAvailable || shouldMount) return;

    const mount = () => setShouldMount(true);

    // Trigger 1: focusin on any form input — captures keyboard-tab into form
    // fields as well as mouse/touch clicks on inputs.
    const onFocusIn = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (
        target?.matches?.(
          'input:not([type="hidden"]), textarea, select, button[type="submit"]',
        )
      ) {
        mount();
      }
    };

    // Trigger 2: pointerdown on submit-style buttons — captures clicks on
    // CTAs that scroll-to-form before any input is focused.
    const onPointerDown = (e: PointerEvent) => {
      const target = (e.target as HTMLElement | null)?.closest?.(
        'button[type="submit"], button[type="button"]',
      );
      if (target) mount();
    };

    document.addEventListener("focusin", onFocusIn, { passive: true });
    document.addEventListener("pointerdown", onPointerDown, { passive: true });

    // Trigger 3: timed fallback — warm the widget for lingering visitors.
    const fallback = window.setTimeout(mount, FALLBACK_MOUNT_DELAY_MS);

    return () => {
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("pointerdown", onPointerDown);
      window.clearTimeout(fallback);
    };
  }, [isReCaptchaAvailable, shouldMount]);

  const isRecaptchaVarified = useCallback(async (): Promise<{
    success: boolean;
    token?: string;
  }> => {
    if (!isReCaptchaAvailable) return { success: true };

    // On-demand mount: a form is submitting before any of our interaction
    // triggers fired. Mount synchronously, then poll until React has
    // attached the ref (or we hit the ceiling). Costs ~500-1500 ms on this
    // one submit and only on this one submit.
    if (!reCaptchaRef.current) {
      setShouldMount(true);
      for (let i = 0; i < REF_POLL_MAX_ATTEMPTS; i++) {
        await new Promise((resolve) =>
          setTimeout(resolve, REF_POLL_INTERVAL_MS),
        );
        if (reCaptchaRef.current) break;
      }
    }

    const reCaptchaToken = await reCaptchaRef.current?.executeAsync();

    if (!reCaptchaToken) {
      tNotifications.error({
        title: "Error!",
        message: "Bot Verfication Captcha not verified",
      });
      return { success: false };
    }

    return { success: true, token: reCaptchaToken };
  }, [isReCaptchaAvailable]);

  const resetReCaptcha = useCallback(() => {
    reCaptchaRef.current?.reset();
  }, []);

  return (
    <ReCaptchaContext.Provider
      value={{
        isReCaptchaAvailable,
        isRecaptchaVarified,
        resetReCaptcha,
        reCaptchaRef,
      }}
    >
      {children}
      {isReCaptchaAvailable && shouldMount && (
        <ReCAPTCHA
          sitekey={env.NEXT_PUBLIC_GOOGLE_RECAPTCHA_SITE_KEY}
          ref={reCaptchaRef}
          size="invisible"
        />
      )}
    </ReCaptchaContext.Provider>
  );
};

export default ReCaptchaProvider;
